import type { ReactNode } from 'react'
import { Alert } from '@digdir/designsystemet-react'
import { Icon, type IconName } from './Icon'

// ---------------------------------------------------------------------------
// Banner — presentational inline notice (props in → UI out).
//
// A compact callout with a leading icon badge, a title, a body, and an
// optional actions row. Three tones: `info` (neutral notice), `ai` (the
// accent AI-assist treatment), and `warn` (the citizen must act).
// Designsystemet's Alert owns appearance; banner classes retain the layout.
// No data fetching, no auth logic, no surface assumptions —
// safe for the public sessionless register bundle.
// ---------------------------------------------------------------------------

export type BannerTone = 'ai' | 'info' | 'warn' | 'danger'

export interface BannerProps {
  /** Visual tone: `ai` (gradient assist), `info` (neutral notice), or `warn` (citizen must act). */
  tone?: BannerTone
  /** Title line (already i18n'd by the caller). */
  title: string
  /** Body copy (already i18n'd). May be a string or arbitrary ReactNode. */
  body: ReactNode
  /** Optional actions row (e.g. buttons) rendered below the body. */
  actions?: ReactNode
  /**
   * Icon shown in the leading badge. Defaults to `sparkle` for the `ai`
   * tone, `warn` for the `warn` tone, and `info` for the `info` tone.
   */
  icon?: IconName
  /** Use alert for actionable errors; informational banners remain notes. */
  role?: 'note' | 'alert' | 'status'
}

/**
 * Inline banner / callout.
 *
 * Usage:
 *   <Banner
 *     tone="ai"
 *     title={t('admin.cases.placeholders.ai_assist.title')}
 *     body={t('admin.cases.placeholders.ai_assist.body')}
 *   />
 */
export function Banner({ tone = 'info', title, body, actions, icon, role = 'note' }: BannerProps) {
  const resolvedIcon: IconName = icon ?? (tone === 'warn' || tone === 'danger' ? 'warn' : tone === 'ai' ? 'sparkle' : 'info')

  return (
    <Alert
      data-color={tone === 'warn' ? 'warning' : tone === 'danger' ? 'danger' : tone === 'ai' ? 'accent' : 'info'}
      className={tone === 'ai' ? 'banner banner--ai' : tone === 'warn' ? 'banner banner--warn' : 'banner'}
      role={role}
    >
      <span className="banner__icon" aria-hidden="true">
        <Icon name={resolvedIcon} size={13} />
      </span>
      <div>
        <div className="banner__title">{title}</div>
        <div className="banner__body">{body}</div>
        {actions && <div className="banner__actions">{actions}</div>}
      </div>
    </Alert>
  )
}
